import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

// ============================================================================
// Type Definitions
// ============================================================================

/** ADF Element from database */
export interface AdfElement {
  id: string;
  slug: string;
  tag_name: string;
  display_name: string;
  description: string | null;
  attributes: Json | null;
  is_required: boolean;
  is_repeatable: boolean;
  is_active: boolean;
  parent_element_id: string | null;
  example_value: string | null;
  alternate_example_value: string | null;
  alternate_context: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

/** ADF Element with children for tree structure */
export interface AdfElementNode extends AdfElement {
  children: AdfElementNode[];
  parent?: AdfElementNode;
}

/** Field mapping with joined field library data */
export interface AdfFieldMappingWithField {
  id: string;
  field_id: string;
  adf_element_id: string;
  parent_path: string;
  is_required: boolean;
  display_order: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  field: {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    data_type: string;
    format: string | null;
    pattern: string | null;
    adf_tag: string | null;
    adf_attributes: Json | null;
  };
  element: {
    id: string;
    slug: string;
    tag_name: string;
    display_name: string;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Converts flat element array into hierarchical tree structure.
 * Handles orphaned elements by treating them as roots.
 */
export function buildElementTree(elements: AdfElement[]): AdfElementNode[] {
  const elementMap = new Map<string, AdfElementNode>();
  const roots: AdfElementNode[] = [];

  // First pass: create node objects with empty children arrays
  elements.forEach((el) => {
    elementMap.set(el.id, { ...el, children: [] });
  });

  // Second pass: build parent-child relationships
  elements.forEach((el) => {
    const node = elementMap.get(el.id)!;
    if (el.parent_element_id) {
      const parent = elementMap.get(el.parent_element_id);
      if (parent) {
        parent.children.push(node);
        node.parent = parent;
      } else {
        // Orphan element - treat as root
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  // Recursively sort children by display_order
  const sortChildren = (nodes: AdfElementNode[]) => {
    nodes.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
    nodes.forEach((node) => sortChildren(node.children));
  };
  sortChildren(roots);

  return roots;
}

/**
 * Generates the XML path string for an element by traversing up the tree.
 * Example: "adf/prospect/customer/contact"
 */
export function getElementPath(element: AdfElementNode): string {
  const parts: string[] = [];
  let current: AdfElementNode | undefined = element;

  while (current) {
    parts.unshift(current.slug);
    current = current.parent;
  }

  return parts.join('/');
}

/**
 * Finds an element in the tree by slug.
 */
export function findElementBySlug(
  nodes: AdfElementNode[],
  slug: string
): AdfElementNode | null {
  for (const node of nodes) {
    if (node.slug === slug) return node;
    const found = findElementBySlug(node.children, slug);
    if (found) return found;
  }
  return null;
}

/**
 * Flattens a tree back to an array (useful for searching).
 */
export function flattenTree(nodes: AdfElementNode[]): AdfElementNode[] {
  const result: AdfElementNode[] = [];
  const traverse = (nodeList: AdfElementNode[]) => {
    for (const node of nodeList) {
      result.push(node);
      traverse(node.children);
    }
  };
  traverse(nodes);
  return result;
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetches all active ADF elements as a flat list.
 * Cached for 10 minutes.
 */
export function useAdfElements() {
  return useQuery({
    queryKey: ['adf-elements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('adf_elements')
        .select('id, slug, tag_name, display_name, description, attributes, is_required, is_repeatable, is_active, parent_element_id, example_value, alternate_example_value, alternate_context, display_order, created_at, updated_at')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      return data as AdfElement[];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Fetches all active ADF elements and builds a hierarchical tree structure.
 * The root of the tree is typically the `adf` element.
 * Cached for 10 minutes.
 */
export function useAdfElementTree() {
  return useQuery({
    queryKey: ['adf-element-tree'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('adf_elements')
        .select('id, slug, tag_name, display_name, description, attributes, is_required, is_repeatable, is_active, parent_element_id, example_value, alternate_example_value, alternate_context, display_order, created_at, updated_at')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      return buildElementTree(data as AdfElement[]);
    },
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Fetches a single ADF element by slug with its direct children.
 * Useful for displaying element details with navigation to children.
 */
export function useAdfElement(slug: string) {
  return useQuery({
    queryKey: ['adf-element', slug],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      // Get the element by slug
      const { data: element, error } = await supabase
        .from('adf_elements')
        .select('id, slug, tag_name, display_name, description, attributes, is_required, is_repeatable, is_active, parent_element_id, example_value, alternate_example_value, alternate_context, display_order, created_at, updated_at')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) throw error;

      // Get direct children
      const { data: children, error: childrenError } = await supabase
        .from('adf_elements')
        .select('id, slug, tag_name, display_name, description, attributes, is_required, is_repeatable, is_active, parent_element_id, example_value, alternate_example_value, alternate_context, display_order, created_at, updated_at')
        .eq('parent_element_id', element.id)
        .eq('is_active', true)
        .order('display_order');

      if (childrenError) throw childrenError;

      return {
        ...element,
        children: (children || []) as AdfElement[],
      } as AdfElementNode;
    },
    enabled: !!slug,
  });
}

/**
 * Fetches all field-to-element mappings with joined field library and element data.
 * Returns enriched mapping objects with full field and element details.
 * Cached for 10 minutes.
 */
export function useAdfFieldMappings() {
  return useQuery({
    queryKey: ['adf-field-mappings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('adf_field_mappings')
        .select(`
          id,
          field_id,
          adf_element_id,
          parent_path,
          is_required,
          display_order,
          notes,
          created_at,
          updated_at,
          field_library (
            id, slug, name, description, data_type,
            format, pattern, adf_tag, adf_attributes
          ),
          adf_elements (
            id, slug, tag_name, display_name
          )
        `)
        .order('parent_path')
        .order('display_order');

      if (error) throw error;

      // Transform to cleaner structure
      return data.map((item) => ({
        id: item.id,
        field_id: item.field_id,
        adf_element_id: item.adf_element_id,
        parent_path: item.parent_path,
        is_required: item.is_required,
        display_order: item.display_order,
        notes: item.notes,
        created_at: item.created_at,
        updated_at: item.updated_at,
        field: item.field_library as AdfFieldMappingWithField['field'],
        element: item.adf_elements as AdfFieldMappingWithField['element'],
      })) as AdfFieldMappingWithField[];
    },
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Fetches fields mapped to a specific ADF element by element ID.
 * Includes full field library details for each mapping.
 */
export function useAdfFieldsByElement(elementId: string) {
  return useQuery({
    queryKey: ['adf-fields-by-element', elementId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('adf_field_mappings')
        .select(`
          id,
          field_id,
          adf_element_id,
          parent_path,
          is_required,
          display_order,
          notes,
          created_at,
          updated_at,
          field_library (
            id, slug, name, description, data_type,
            format, pattern, adf_tag, adf_attributes,
            min_length, max_length, minimum, maximum
          )
        `)
        .eq('adf_element_id', elementId)
        .order('display_order');

      if (error) throw error;

      return data.map((item) => ({
        ...item,
        field: item.field_library,
      }));
    },
    enabled: !!elementId,
  });
}

/**
 * Fetches fields by their XML parent path.
 * Example: useAdfFieldsByPath('prospect/customer/contact')
 * Useful for rendering fields within a specific section of the ADF structure.
 */
export function useAdfFieldsByPath(parentPath: string) {
  return useQuery({
    queryKey: ['adf-fields-by-path', parentPath],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('adf_field_mappings')
        .select(`
          id,
          field_id,
          adf_element_id,
          parent_path,
          is_required,
          display_order,
          notes,
          created_at,
          updated_at,
          field_library (
            id, slug, name, description, data_type,
            format, pattern, adf_tag, adf_attributes,
            min_length, max_length, minimum, maximum
          ),
          adf_elements (
            id, slug, tag_name, display_name
          )
        `)
        .eq('parent_path', parentPath)
        .order('display_order');

      if (error) throw error;

      return data.map((item) => ({
        ...item,
        field: item.field_library,
        element: item.adf_elements,
      }));
    },
    enabled: !!parentPath,
  });
}

/**
 * Fetches fields that match a partial path pattern.
 * Example: useAdfFieldsByPathPattern('prospect/customer') matches all customer-related fields.
 */
export function useAdfFieldsByPathPattern(pathPattern: string) {
  return useQuery({
    queryKey: ['adf-fields-by-path-pattern', pathPattern],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('adf_field_mappings')
        .select(`
          id,
          field_id,
          adf_element_id,
          parent_path,
          is_required,
          display_order,
          notes,
          field_library (
            id, slug, name, description, data_type,
            format, pattern, adf_tag, adf_attributes
          ),
          adf_elements (
            id, slug, tag_name, display_name
          )
        `)
        .ilike('parent_path', `${pathPattern}%`)
        .order('parent_path')
        .order('display_order');

      if (error) throw error;

      return data.map((item) => ({
        ...item,
        field: item.field_library,
        element: item.adf_elements,
      }));
    },
    enabled: !!pathPattern,
  });
}
